import { changeBoard, lockViolation } from './manualScheduleBoard';
import { arrangeEntries } from './scheduleOperations';
import { cleanParallel } from './scheduleTimeline';
import { visitMetadata } from './locationVisits';

export const locationTimeline = day => {
  const rows=[];
  for(let i=0;i<=(day.entries||[]).length;i++) {
    (day.locationVisits||[]).filter(v=>v.position===i).forEach(visit=>rows.push({visit}));
    if(day.entries[i]) rows.push({entry:day.entries[i],entryIndex:i});
  }
  return rows;
};
export const timelineGap = (day,index,excludedVisit) => locationTimeline(day).filter(r=>r.visit?.id!==excludedVisit).slice(0,index).filter(r=>r.entry).length;
const changesFor=(days,next)=>next.filter((d,i)=>d!==days[i]).map(day=>({dayNumber:day.dayNumber,day}));
export const editVisit = (days,n,visit,location) => {
  const day=days.find(d=>d.dayNumber===n);
  if(!day||!visit.id||!location?.id||visit.position<0||visit.position>(day.entries||[]).length)return null;
  const locations=[...(day.locations||[]).filter(l=>l.id!==location.id),location];
  const locationVisits=[...(day.locationVisits||[]).filter(v=>v.id!==visit.id),{...visit,locationRef:location.id}].sort((a,b)=>a.position-b.position);
  return [{dayNumber:n,day:{...day,locations,locationVisits}}];
};
export const positionVisit = (days,n,id,position) => {
  const day=days.find(d=>d.dayNumber===n),visit=day?.locationVisits?.find(v=>v.id===id);
  return visit?editVisit(days,n,{...visit,position},day.locations?.find(l=>l.id===visit.locationRef)):null;
};
export const linkVisit = (days,n,id,occurrenceIds) => {
  const day=days.find(d=>d.dayNumber===n);
  if(!day?.locationVisits?.some(v=>v.id===id)||occurrenceIds.some(oid=>!day.entries.some(e=>e.id===oid)))return null;
  // Relinking is explicit: detach the chosen occurrences from their previous visit.
  const locationVisits=day.locationVisits.map(v=>({...v,occurrenceIds:v.id===id?[...new Set(occurrenceIds)]:v.occurrenceIds.filter(oid=>!occurrenceIds.includes(oid))}));
  return [{dayNumber:n,day:{...day,locationVisits}}];
};
export const dropIntoVisit = (days,n,id,from,entryId,addition,op=changeBoard) => {
  const target=days.find(d=>d.dayNumber===n),visit=target?.locationVisits?.find(v=>v.id===id);
  if(!visit)return null;
  let next=days;
  if(from!==n) {
    const index=days.find(d=>d.dayNumber===from)?.entries.findIndex(e=>e.id===entryId)??0;
    const moved=op(days,from,index,n,visit.position,addition);
    if(!moved)return null;
    next=days.map(d=>moved.find(c=>c.dayNumber===d.dayNumber)?.day||d).map(d=>({...d,...visitMetadata(d)}));
  }
  const updated=next.find(d=>d.dayNumber===n),current=updated.locationVisits.find(v=>v.id===id);
  const linked=linkVisit(next,n,id,[...current.occurrenceIds,addition?.id||entryId]);
  if(!linked)return null;
  next=next.map(d=>linked.find(c=>c.dayNumber===d.dayNumber)?.day||d);
  return changesFor(days,next);
};
// Same-day Linked Group: the card and its linked occurrences (activities only when chosen) move
// together to the gap before the gap-th entry, keeping their relative order, IDs, durations and links.
// Moved entries drop computed/manual starts so times recalculate from their new position (overtime allowed).
export const moveGroupWithinDay = (days,n,id,gap,includeActivities=false) => {
  const day=days.find(d=>d.dayNumber===n),visit=day?.locationVisits?.find(v=>v.id===id);
  if(!visit)return {error:'That Location Card is no longer there. Refresh and try again.'};
  const entries=day.entries||[],movingIds=new Set(entries.filter(e=>visit.occurrenceIds.includes(e.id)&&(e.kind==='scene'||includeActivities)).map(e=>e.id));
  if(!movingIds.size)return {error:'This Location Card has no linked scenes to move. Use Card Only, or link scenes first.'};
  const locked=entries.find(e=>movingIds.has(e.id)&&e.locked);
  if(locked)return {error:`"${locked.name||'A linked entry'}" is locked. Unlock it before moving the linked group.`};
  const moving=entries.filter(e=>movingIds.has(e.id)),rest=entries.filter(e=>!movingIds.has(e.id));
  const at=entries.slice(0,Math.max(0,Math.min(gap,entries.length))).filter(e=>!movingIds.has(e.id)).length;
  const reordered=cleanParallel([...rest.slice(0,at),...moving,...rest.slice(at)]);
  const nextDays=days.map(d=>d.dayNumber===n?{...d,entries:reordered}:d);
  const lockError=lockViolation(days,nextDays);
  if(lockError)return {error:lockError};
  const original=entries.map(e=>movingIds.has(e.id)?{...e,start:null,manualTime:false}:e);
  const timed=arrangeEntries(reordered.map(e=>movingIds.has(e.id)?{...e,start:null,manualTime:false}:e),true,original).map(e=>entries.find(o=>o.id===e.id&&o.locked)||e);
  // Other cards stay before the same following entry they preceded (or at the end).
  const anchor=v=>{for(let i=v.position;i<entries.length;i++)if(!movingIds.has(entries[i].id))return entries[i].id;return null;};
  const position=v=>{const a=anchor(v);return a?timed.findIndex(e=>e.id===a):timed.length;};
  const locationVisits=day.locationVisits.map(v=>v.id===id?{...v,position:at}:{...v,position:position(v)}).sort((a,b)=>a.position-b.position);
  return {changes:[{dayNumber:n,day:{...day,entries:timed,locationVisits}}]};
};
export const moveVisit = (days,from,id,to,mode,includeActivities=false,op=changeBoard) => {
  const source=days.find(d=>d.dayNumber===from),destination=days.find(d=>d.dayNumber===to);
  const visit=source?.locationVisits?.find(v=>v.id===id),location=source?.locations?.find(l=>l.id===visit?.locationRef);
  if(!visit||!location||!destination||from===to)return null;
  const moving=mode==='group'?source.entries.filter(e=>visit.occurrenceIds.includes(e.id)&&(e.kind==='scene'||includeActivities)):[];
  let next=days;
  for(const entry of moving) {
    const current=next.find(d=>d.dayNumber===from);
    const result=op(next,from,current.entries.findIndex(e=>e.id===entry.id),to,next.find(d=>d.dayNumber===to).entries.length);
    if(!result)return null;
    next=next.map(d=>result.find(c=>c.dayNumber===d.dayNumber)?.day||d);
  }
  next=next.map(d=> {
    if(d.dayNumber===from)return {...d,...visitMetadata(d),locationVisits:(d.locationVisits||[]).filter(v=>v.id!==id).map(v=>({...v,position:Math.min(v.position,d.entries.length),occurrenceIds:v.occurrenceIds.filter(oid=>d.entries.some(e=>e.id===oid))}))};
    if(d.dayNumber===to)return {...d,...visitMetadata(d),locations:d.locations?.some(l=>l.id===location.id)?d.locations:[...(d.locations||[]),{...location}],locationVisits:[...(d.locationVisits||[]),{...visit,position:destination.entries.length,occurrenceIds:moving.map(e=>e.id)}]};
    return d;
  });
  return changesFor(days,next);
};// Board operation for the AI draft: same lock rules as rearrangeDraft, changes returned like changeBoard.
export const draftBoard = rearrange => (days,from,index,to,targetIndex) => {
  if(from==null)return null;
  const next=rearrange(days,from,index,to,targetIndex);
  if(next===days)return null;
  return next.filter(d=>d.dayNumber===from||d.dayNumber===to).map(day=>({dayNumber:day.dayNumber,day}));
};
